import * as React from "react";
import type { ProfileData } from "@recursive-robot/react-jsx-parser";
import { useAppSelector, useThunkDispatch } from "../../store";
import { profilingBatchReceived } from "../../store/profiling/actions";

/**
 * Wires a `JsxParser` instance into the profiling store.
 *
 * `onProfile` is invoked by the parser **during React's render phase** (and, for `callback`/`react`
 * batches, synchronously as those fire), so it must never dispatch synchronously — doing so triggers
 * React's "cannot update while rendering" warning.  Instead batches are buffered in a ref and flushed
 * on a microtask.  The callback has a stable identity so it never invalidates a memoized parser.
 *
 * The hook returns the two enable flags alongside the sink so callers gate the parser props:
 *   `onProfile={enabled ? onProfile : undefined}` (presence = parser profiling on; omit = zero overhead)
 *   `profileReactRender={enabled && reactRenderEnabled}`.
 *
 * Note: `Canvas` / `Template` select only these two booleans — never the batch data — so a batch
 * dispatch does not re-render the parser, avoiding a profile -> dispatch -> profile loop.
 */
export function useProfilingSink() {
    const dispatch = useThunkDispatch();
    const enabled = useAppSelector(s => s.profiling.enabled);
    const reactRenderEnabled = useAppSelector(s => s.profiling.reactRenderEnabled);

    const buffer = React.useRef<ProfileData[]>([]);
    const scheduled = React.useRef(false);

    const onProfile = React.useCallback((batch: ProfileData) => {
        buffer.current.push(batch);
        if (!scheduled.current) {
            scheduled.current = true;
            queueMicrotask(() => {
                scheduled.current = false;
                const batches = buffer.current;
                buffer.current = [];
                batches.forEach(b => dispatch(profilingBatchReceived(b)));
            });
        }
    }, [dispatch]);

    return { onProfile, enabled, reactRenderEnabled };
}
